// Considered: `HomeMode`, `CompactTimelineMolecule` — these are what this organism retires
// (specs/stream-trajectory-tools.md § Design System Reuse Audit). Neither folds by correlationId,
// shows provenance, or offers acts in place; both run their own independent `/timeline` fetch.
// This owns nothing fetch-wise itself — it reads `ToolbarContext.streamState`.
//
// Source: design/metaspace-tools/metaspace-tools-v2.jsx:108-279 (`StreamPanelV2`). IA: header
// (filter glyph + Open sphere) -> optional facet bar -> day chips -> entries -> chat cluster.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { InspectorAct, InspectorPayload } from 'types/inspector';
import type { StreamChord } from 'types/stream';
import { useToolbarContextOrNull, type ConversationMessage, type StreamQuery } from '../../../context/ToolbarContext';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useConversation } from '../../molecules/SphereChatPanel/UseConversation';
import { buildThreadPayload } from '../../../helpers/stream/buildStreamInspectorPayloads';
import { getContextViewState, putContextViewState } from '../../../api/contextApi';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import ToolInspectorOrganism from '../ToolInspector/ToolInspectorOrganism';
import StreamFacetBarMolecule, { type StreamLensOption } from '../../molecules/StreamFacetBar/StreamFacetBarMolecule';
import StreamDayChipAtom from '../../atoms/StreamDayChip/StreamDayChipAtom';
import StreamEntryMolecule from '../../molecules/StreamEntry/StreamEntryMolecule';
import StreamChatClusterMolecule from '../../molecules/StreamChatCluster/StreamChatClusterMolecule';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import './StreamPanelOrganism.scss';

export interface StreamPanelOrganismProps {
  sphereId: string;
  /** `SphereChatPanel`'s contextKey convention — reused so the chat cluster's reply posts to the same thread. */
  contextKey?: string;
  onOpenSphere?: () => void;
  onOpenComments?: () => void;
  onJumpToTrack?: (aspectSystemName: string) => void;
  /** Scope the stream to one aspect's entries (Trajectory band click → "Scope Stream"). */
  scopeAspect?: string | null;
  /** Clears `scopeAspect` (the scope chip's dismiss). */
  onClearScope?: () => void;
  className?: string;
}

/** Stable lens ids. These are the persisted `context_view_state` key and the `stream_lens_applied`
 * analytics dimension, so they must stay language-independent — the chip copy lives in
 * `stream.lenses.*` and can change (or be translated) without orphaning a stored view. */
export const STREAM_LENS_IDS = ['machine_writes', 'human_attestations', 'imported', 'calculated'] as const;
export type StreamLensId = (typeof STREAM_LENS_IDS)[number];

/** Server-side filter each lens applies (`GET /spheres/:id/stream` params). `inferred` folds into
 * the calculated chip and `migrated` into imported provenance, mirroring `resolveStreamProvenanceChips`. */
const LENS_QUERY: Record<StreamLensId, StreamQuery> = {
  machine_writes: { actorKind: 'system' },
  human_attestations: { provenance: ['attested'] },
  imported: { provenance: ['imported', 'migrated'] },
  calculated: { provenance: ['calculated', 'inferred'] },
};

function formatRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  if (hours >= 1) return `${hours}h`;
  const minutes = Math.max(0, Math.floor(ms / (1000 * 60)));
  return `${minutes}m`;
}

function dayKeyOf(iso: string): string {
  return iso.slice(0, 10);
}

const StreamPanelOrganism: React.FC<StreamPanelOrganismProps> = ({
  sphereId,
  contextKey,
  onOpenSphere,
  onOpenComments,
  onJumpToTrack,
  scopeAspect,
  onClearScope,
  className,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logAnalyticsEvent = useAnalytics();
  const apiClients = useApiClientsOrNull();
  const toolbarContext = useToolbarContextOrNull();
  const conversation = useConversation(contextKey);

  const [facetsOpen, setFacetsOpen] = useState(false);
  const [activeLens, setActiveLens] = useState<string | null>(null);
  const [collapsedDays, setCollapsedDays] = useState<Record<string, boolean>>({});
  const [foldedEntries, setFoldedEntries] = useState<Record<string, boolean>>({});
  const [inspectorItem, setInspectorItem] = useState<InspectorPayload | null>(null);

  const lenses = useMemo<StreamLensOption[]>(
    () => STREAM_LENS_IDS.map((id) => ({ id, label: t(`stream.lenses.${id}`) })),
    [t],
  );
  const activeLensLabel = lenses.find((lens) => lens.id === activeLens)?.label ?? null;

  const { chords, isLoading, error } = toolbarContext?.streamState ?? { chords: [], isLoading: false, error: null };
  const conversationMessages = toolbarContext?.conversationState.messages;

  const contextRef = `stream:${sphereId}`;

  // Restore the caller's last-applied lens (context-view-state is per-owner, so this is the
  // "active lens" persistence the spec's Views section asks for — one GET on mount, mirroring
  // TrajectoryPanelOrganism's pin restore).
  useEffect(() => {
    let cancelled = false;
    if (!apiClients?.schemaClient) return undefined;
    getContextViewState(apiClients.schemaClient, contextRef)
      .then((state) => {
        if (!cancelled && state?.viewConfig?.lens) {
          setActiveLens(state.viewConfig.lens as string);
          setFacetsOpen(true);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextRef]);

  // Push the active lens + aspect scope to the server-side stream query — the lens is a real
  // filter (provenance/actorKind params), not a client-side decoration. ToolbarContext refetches
  // page 1 whenever this changes.
  const setStreamQuery = toolbarContext?.setStreamQuery;
  useEffect(() => {
    if (!setStreamQuery) return;
    const lensQuery = activeLens ? (LENS_QUERY[activeLens as StreamLensId] ?? {}) : {};
    setStreamQuery({ ...lensQuery, aspects: scopeAspect ? [scopeAspect] : undefined });
  }, [activeLens, scopeAspect, setStreamQuery]);

  const toggleDay = (day: string) => setCollapsedDays((prev) => ({ ...prev, [day]: !prev[day] }));
  const toggleFold = (correlationId: string) =>
    setFoldedEntries((prev) => ({ ...prev, [correlationId]: !(prev[correlationId] ?? true) }));

  // Group chat messages (already fetched into ToolbarContext.conversationState for the 'stream'
  // panel) by day, merged in as quiet entries alongside the ledger chords — never a separate fetch.
  const messagesByDay = useMemo(() => {
    const map = new Map<string, ConversationMessage[]>();
    for (const message of conversationMessages ?? []) {
      const day = dayKeyOf(message.created_at);
      const bucket = map.get(day) ?? [];
      bucket.push(message);
      map.set(day, bucket);
    }
    return map;
  }, [conversationMessages]);

  // Days in server order (chords are already day-grouped/sorted), plus any chat-only days.
  const days = useMemo(() => {
    const seen: string[] = [];
    for (const chord of chords) {
      if (!seen.includes(chord.day)) seen.push(chord.day);
    }
    for (const day of messagesByDay.keys()) {
      if (!seen.includes(day)) seen.push(day);
    }
    return seen;
  }, [chords, messagesByDay]);

  // Aspect labels now arrive resolved on each entry, so the scope chip reads one off the loaded
  // page rather than re-resolving it. Falls back to the system name when the scoped aspect has no
  // entry in view (an empty scoped result, most obviously).
  const aspectLabels = useMemo(() => {
    const byName = new Map<string, string>();
    for (const chord of chords) {
      for (const entry of [chord.root, ...chord.members, ...chord.consequences]) {
        if (entry.aspectSystemName && entry.aspectLabel && !byName.has(entry.aspectSystemName)) {
          byName.set(entry.aspectSystemName, entry.aspectLabel);
        }
      }
    }
    return byName;
  }, [chords]);

  const chordsByDay = useMemo(() => {
    const map = new Map<string, StreamChord[]>();
    for (const chord of chords) {
      const bucket = map.get(chord.day) ?? [];
      bucket.push(chord);
      map.set(chord.day, bucket);
    }
    return map;
  }, [chords]);

  const handleInspect = (payload: InspectorPayload) => {
    setInspectorItem(payload);
    logAnalyticsEvent('stream_entry_inspected', undefined, {
      kind: payload.kind,
      folded: false,
    });
  };

  const handleCloseInspector = () => setInspectorItem(null);

  const handleCopyLink = (address: string) => {
    const url = `${globalThis.window.location.origin}${address}`;
    navigator.clipboard?.writeText(url).catch(() => undefined);
    logAnalyticsEvent('chord_link_copied', undefined, { kind: inspectorItem?.kind });
  };

  const handleAct = async (act: InspectorAct, item: InspectorPayload, inputValue?: string): Promise<void> => {
    if (!apiClients?.graphClient) throw new Error('API clients not available');

    switch (act.id) {
      case 'correct': {
        const typed = inputValue?.trim();
        if (!typed || !item.context.aspectSystemName || !item.context.propertyName) {
          throw new Error('Correction value missing');
        }
        const value = act.input?.kind === 'number' ? Number(typed) : typed;
        if (act.input?.kind === 'number' && Number.isNaN(value)) throw new Error('Correction value not a number');
        await apiClients.graphClient.post(`/spheres/${sphereId}/observations`, {
          aspectSystemName: item.context.aspectSystemName,
          propertyName: item.context.propertyName,
          value,
        });
        logAnalyticsEvent('stream_act_performed', undefined, {
          act: 'correct',
          aspectSystemName: item.context.aspectSystemName,
        });
        await toolbarContext?.refreshStream();
        return;
      }
      case 'reply': {
        const typed = inputValue?.trim();
        if (!typed) throw new Error('Reply text missing');
        await conversation.sendMessage(typed);
        logAnalyticsEvent('stream_act_performed', undefined, { act: 'reply' });
        await toolbarContext?.refreshConversations();
        return;
      }
      case 'attest':
      case 'reject': {
        await apiClients.graphClient.post(`/spheres/${sphereId}/validations`, {
          aspectSystemName: item.context.aspectSystemName,
          correlationId: item.context.correlationId,
          status: act.id === 'attest' ? 'validated' : 'rejected',
        });
        logAnalyticsEvent('stream_act_performed', undefined, {
          act: act.id,
          aspectSystemName: item.context.aspectSystemName,
        });
        await toolbarContext?.refreshStream();
        return;
      }
      case 'copy_link':
        handleCopyLink(item.address);
        return;
      case 'open_sphere':
        onOpenSphere?.();
        return;
      case 'open_identity_sphere':
        // Navigates to the ACTOR's identity sphere (`buildIdentityPayload`'s `/identity/:id`
        // address) — distinct from `open_sphere`, which opens the panel's own tracked sphere.
        navigate(item.address);
        return;
      case 'filter_to_calculated':
        setActiveLens('calculated');
        setFacetsOpen(true);
        return;
      case 'filter_to_imported':
        setActiveLens('imported');
        setFacetsOpen(true);
        return;
      case 'create_view': {
        await apiClients.graphClient.post('/context-views', {
          viewType: 'stream',
          ownerSphereType: 'identity',
          viewConfig: { lens: activeLens, facets: facetsOpen },
        });
        logAnalyticsEvent('view_saved', undefined, { viewType: 'stream' });
        return;
      }
      default:
        return;
    }
  };

  const handleSaveAsView = () => {
    setInspectorItem({
      kind: 'view',
      icon: 'lucide:bookmark',
      title: activeLensLabel
        ? t('toolInspector.view.titleWithLens', { lens: activeLensLabel, defaultValue: `Save “${activeLensLabel}” as a View` })
        : t('toolInspector.view.title', { defaultValue: 'Save this lens as a View' }),
      subtitle: t('toolInspector.view.subtitle', {
        defaultValue: 'A View is a named, addressable, recallable lens — not an ephemeral toggle.',
      }),
      address: `/sphere/${sphereId}/views/new`,
      acts: [{
        id: 'create_view',
        label: t('stream.acts.createView'),
        variant: 'primary',
        icon: 'lucide:check',
        confirmationLabel: t('stream.acts.viewSavedConfirmation', {
          defaultValue: 'View saved — named, addressable, recallable',
        }),
      }],
      context: { sphereId },
    });
  };

  const handleSelectLens = (lens: string | null) => {
    setActiveLens(lens);
    // Dimension carries the stable id, not the rendered label — see STREAM_LENS_IDS.
    logAnalyticsEvent('stream_lens_applied', undefined, { lens });
    if (apiClients?.schemaClient) {
      putContextViewState(apiClients.schemaClient, contextRef, { viewConfig: { lens } }).catch(() => undefined);
    }
  };

  const handleReply = async (content: string) => {
    await conversation.sendMessage(content);
  };

  return (
    <div className={['stream-panel-organism', className].filter(Boolean).join(' ')}>
      <div className="stream-panel-organism__header">
        <span className="stream-panel-organism__title">
          <GlyphAtom glyph="lucide:align-left" />
          {t('stream.panel.title')}
        </span>
        <span className="stream-panel-organism__header-actions">
          <button
            type="button"
            className={['stream-panel-organism__filter-toggle', (facetsOpen || activeLens) && 'stream-panel-organism__filter-toggle--active']
              .filter(Boolean)
              .join(' ')}
            onClick={() => setFacetsOpen((f) => !f)}
            aria-label={t('stream.panel.facetsPeek')}
            title={t('stream.panel.facetsPeek')}
          >
            <GlyphAtom glyph="lucide:filter" />
          </button>
          <button type="button" className="stream-panel-organism__open-sphere" onClick={onOpenSphere}>
            {t('stream.panel.openSphere')} <GlyphAtom glyph="lucide:arrow-up-right" />
          </button>
        </span>
      </div>

      {inspectorItem ? (
        <ToolInspectorOrganism
          item={inspectorItem}
          backLabel={t('stream.panel.title')}
          onClose={handleCloseInspector}
          onAct={handleAct}
          onCopyLink={handleCopyLink}
        />
      ) : (
        <>
          {facetsOpen && (
            <StreamFacetBarMolecule
              lenses={lenses}
              activeLens={activeLens}
              onSelectLens={handleSelectLens}
              onSaveAsView={handleSaveAsView}
            />
          )}

          {scopeAspect && (
            <div className="stream-panel-organism__scope-row">
              <ChipAtom
                icon="lucide:crosshair"
                label={t('stream.scope.chip', {
                  aspect: aspectLabels.get(scopeAspect) || scopeAspect,
                  defaultValue: 'Scoped to {{aspect}}',
                })}
                tone="neutral"
                removable
                onRemove={() => onClearScope?.()}
              />
            </div>
          )}

          <div className="stream-panel-organism__body">
            {days.length === 0 && !isLoading && (
              <EmptyStateAtom icon="pi-inbox" title={t('stream.empty.title')} description={t('stream.empty.description')} />
            )}

            {error && <div className="stream-panel-organism__error">{error}</div>}

            {days.map((day) => {
              const dayChords = chordsByDay.get(day) ?? [];
              const dayMessages = messagesByDay.get(day) ?? [];
              const collapsed = collapsedDays[day] ?? false;
              const dayLabel = new Date(day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }).toUpperCase();

              return (
                <StreamDayChipAtom key={day} label={dayLabel} collapsed={collapsed} onToggle={() => toggleDay(day)}>
                  <div className="stream-panel-organism__day-entries">
                    {dayChords.map((chord) => (
                      <StreamEntryMolecule
                        key={chord.correlationId}
                        chord={chord}
                        sphereId={sphereId}
                        // Server-resolved (`GET /spheres/:id/stream`). This used to call a
                        // client-side cache over an S2S-only route the browser could never
                        // reach, so every entry silently rendered its raw system name.
                        aspectLabel={chord.root.aspectLabel ?? chord.root.aspectSystemName ?? ''}
                        onInspect={handleInspect}
                        folded={foldedEntries[chord.correlationId] ?? true}
                        onToggleFold={() => toggleFold(chord.correlationId)}
                        relativeTime={formatRelativeTime(chord.root.observedAt)}
                        onJumpToTrack={onJumpToTrack}
                      />
                    ))}
                    {dayMessages.length > 0 && (
                      <StreamChatClusterMolecule
                        messages={dayMessages}
                        threadPayload={buildThreadPayload(dayMessages, sphereId, conversation.threadId, t)}
                        onInspect={handleInspect}
                        onReply={handleReply}
                        onOpenComments={() => onOpenComments?.()}
                      />
                    )}
                  </div>
                </StreamDayChipAtom>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

StreamPanelOrganism.displayName = 'StreamPanelOrganism';

export default StreamPanelOrganism;
